import React from 'react';
import { Phone, MessageCircle, MapPin, ArrowUp } from 'lucide-react';
import Logo from './Logo';
import { contactInfo, serviceAreas } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Portfolio', href: '#portfolio' },
    { label: 'About Us', href: '#about' },
    { label: 'Reviews', href: '#reviews' },
    { label: 'Contact', href: '#contact' }
  ];

  const services = [
    'Vacation Rental',
    'Residential',
    'Deep Cleaning',
    'Move-In/Out',
    'Post-Construction',
    'Emergency'
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0F2840]">
      {/* CTA Banner */}
      <div 
        className="py-10 md:py-12"
        style={{ background: 'linear-gradient(135deg, #1A3E5C 0%, #1A4A6E 100%)' }}
      >
        <div className="container">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
            <div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold text-white mb-2">
                Ready to Transform Your Space?
              </h3>
              <p className="text-white/70 text-sm md:text-base">
                Get your free quote today. Professional cleaning starts here.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <a 
                href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
                style={{ background: 'white', color: '#1A3E5C' }}
              >
                <Phone size={16} />
                {contactInfo.phone}
              </a>
              <a 
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
                style={{ background: '#C9A962', color: '#0F2840' }}
              >
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Logo variant="white" size="small" className="mb-4" />
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Professional cleaning and home organization services in San Diego. 
              Attention to detail that makes your home shine.
            </p>
            <div className="flex items-center gap-3">
              <a 
                href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300"
                aria-label="Call us"
              >
                <Phone size={16} />
              </a>
              <a 
                href={`https://wa.me/1${contactInfo.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hello! I'd like to request a free quote for cleaning services.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Navigation</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/60 text-sm hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Services</h4>
            <ul className="space-y-2.5">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-white/60 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Contact</h4>
            <div className="space-y-3">
              <a 
                href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2.5 text-white hover:text-white/80 transition-colors"
              >
                <Phone size={14} className="text-white/50 flex-shrink-0" />
                <span className="text-sm font-medium">{contactInfo.phone}</span>
              </a>
              
              <div className="flex items-center gap-2.5">
                <MapPin size={14} className="text-white/50 flex-shrink-0" />
                <span className="text-white/60 text-sm">San Diego, CA</span>
              </div>

              <div className="pt-3 mt-3 border-t border-white/10">
                <p className="text-white/40 text-xs mb-1.5">Hours</p>
                <p className="text-white/70 text-sm">Mon - Sat: 7AM - 7PM</p>
                <p className="text-sm font-medium mt-1" style={{ color: '#C9A962' }}>
                  Emergency: 24/7
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/40 text-[9px] font-bold uppercase tracking-wider text-center sm:text-left">
              © {currentYear} Grupo Santana Home Services
            </p>
            
            <div className="flex items-center gap-3">
              <span className="text-white/40 text-[9px] font-bold uppercase tracking-wider">
                San Diego & Surrounding Areas
              </span>
              <button
                onClick={scrollToTop}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all duration-300"
                aria-label="Back to top"
              >
                <ArrowUp size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
